import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axiosInstance";
import { fetchSpeakerByIdByUsers } from "../../Api/Users/getUsersApi";
import BlogSidebar from "./BlogSidebar";
import AOS from "aos";
import "aos/dist/aos.css";

const BlogSection = () => {
  const { t, i18n } = useTranslation();
  const [blogData, setBlogData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchArticlesAndAuthors = async () => {
      try {
        const response = await axiosInstance.get("/api/v1/users/articles");
        const articles = response.data.items;

        const authors = {};
        await Promise.all(
          articles.map(async (article) => {
            if (!authors[article.userId]) {
              try {
                const speaker = await fetchSpeakerByIdByUsers(article.userId);
                authors[article.userId] =
                  i18n.language === "ar"
                    ? speaker.arabicFullName
                    : speaker.englishFullName;
              } catch (error) {
                console.error(
                  `Error fetching author for userId ${article.userId}`,
                  error
                );
                authors[article.userId] = t("unknownAuthor");
              }
            }
          })
        );

        const articlesWithAuthors = articles.map((article) => ({
          ...article,
          author: authors[article.userId] || t("loading"),
          title:
            i18n.language === "ar" ? article.arabicTitle : article.englishTitle,
          description:
            i18n.language === "ar"
              ? article.arabicDescription
              : article.englishDescription,
          imageUrl: article.thumbnailImagePath,
          date: article.createdOn,
        }));

        setBlogData(articlesWithAuthors);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching blog data:", error);
        setIsLoading(false);
      }
    };

    fetchArticlesAndAuthors();
  }, [i18n.language, t]);

  const featuredArticle = blogData[0];
  const articles = blogData.slice(1);

  if (isLoading) {
    return (
      <section className="py-5 text-center" data-aos="fade-up">
        <div className="container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </div>
        </div>
      </section>
    );
  }

  const truncateText = (text, maxLength = 75) => {
    if (!text) return "";
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  return (
    <>
      <section className="bg-light py-5" data-aos="fade-up">
        <div className="container py-4">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <h1 className="fw-bold text-primary-custom mb-3">
                {t("blog_title")}
              </h1>
              <p className="lead text-muted">{t("blog_subtitle")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5" data-aos="fade-up">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              {/* Featured Article */}
              {featuredArticle && (
                <div className="mb-5">
                  <h2 className="fw-bold mb-4">{t("featured_articles")}</h2>
                  <div className="card blog-card featured-blog shadow-sm border-0 rounded-4 overflow-hidden">
                    <div className="row g-0">
                      <div className="col-md-6">
                        <img
                          src={featuredArticle.imageUrl}
                          className="card-img h-100"
                          alt={featuredArticle.title}
                          style={{ objectFit: "cover" }}
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="card-body p-4 d-flex flex-column h-100">
                          <h3 className="card-title fw-bold">
                            {featuredArticle.title}
                          </h3>
                          <p className="card-text text-muted my-3">
                            {truncateText(featuredArticle.description)}
                          </p>

                          <div className="mt-auto">
                            <div className="d-flex align-items-center mb-3">
                              {/* <img
                                src={featuredArticle.profileImage}
                                className="rounded-circle me-2"
                                alt={featuredArticle.author}
                              /> */}
                              <div>
                                <small className="text-muted">
                                  {t("author")}
                                </small>
                                <p className="mb-0 small fw-medium">
                                  {featuredArticle.author}
                                </p>
                              </div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <small className="text-muted">
                                {featuredArticle.date?.slice(0, 10)}
                              </small>
                              <a
                                href={`/blog-detail/${featuredArticle.id}`}
                                className="btn btn-sm btn-outline-primary rounded-pill"
                              >
                                {t("read_more")}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Latest Articles */}
              <div className="mb-5">
                <h2 className="fw-bold mb-4">{t("latest_articles")}</h2>
                <div className="row g-4">
                  {articles.map((article) => (
                    <div
                      key={article.id}
                      className="col-md-6"
                      data-aos="fade-up"
                    >
                      <div className="card blog-card h-100 shadow-sm hover-card">
                        <img
                          src={article.imageUrl}
                          className="card-img-top"
                          alt={article.title}
                        />
                        <div className="card-body">
                          <h3 className="card-title h5 fw-bold">
                            {article.title}
                          </h3>
                          <p className="card-text text-muted">
                            {truncateText(article.description)}
                          </p>

                          <div className="d-flex align-items-center mt-3">
                            {/* <img
                              src={article.profileImage}
                              className="rounded-circle me-2"
                              alt={article.author}
                            /> */}
                            <small className="text-muted">
                              {article.author}
                            </small>
                          </div>
                        </div>
                        <div className="card-footer bg-transparent border-0">
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              {article.date?.slice(0, 10)}
                            </small>
                            <a
                              href={`/blog-detail/${article.id}`}
                              className="btn btn-sm btn-link text-primary p-0"
                            >
                              {t("read_more")}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <BlogSidebar />
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogSection;
